import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Grid,
  CardContent,
  Box,
  Avatar,
  Button,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  Divider,
} from '@mui/material';
import {
  Inventory as StockIcon,
  Settings as ConfigIcon,
  AdminPanelSettings as AdminIcon,
  People as HRIcon,
  Assessment as AuditIcon,
  AccountBalance as FinanceIcon,
  LocalShipping as LogisticsIcon,
  ShoppingCart as SalesIcon,
  Build as MaintenanceIcon,
  Logout as LogoutIcon,
  Description as CommercialIcon,
  PrecisionManufacturing as ProductionIcon,
  Storefront as ComptoirIcon,
  Chat as ChatIcon,
  AccountCircle as AccountCircleIcon,
  Brightness4 as Brightness4Icon,
  Brightness7 as Brightness7Icon,
  ReceiptLong as InvoiceIcon,
  EventAvailable as PointageIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { useAuth } from '../contexts/AuthContext';
import { usePermissions } from '../contexts/PermissionContext';
import { useThemeMode } from '../contexts/ThemeContext';
import NotificationsBell from '../components/NotificationsBell';
import HeaderChatButton from '../components/HeaderChatButton';
import { GlassBackdrop, GlassSurface } from '../components/GlassDashboard';
import logo from '../assets/images/logo.png';

interface ERPModule {
  id: string;
  title: string;
  description: string;
  icon: React.ReactElement;
  route: string;
  available: boolean;
  color: string;
  moduleName?: string; // Permission module name
}

const MainDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { logout, user } = useAuth();
  const { canAccessModule, hasPermission } = usePermissions();
  const { mode, toggleTheme } = useThemeMode();
  const [profileAnchorEl, setProfileAnchorEl] = useState<HTMLElement | null>(null);
  const profileOpen = Boolean(profileAnchorEl);

  const erpModules: ERPModule[] = [
    {
      id: 'stock',
      title: 'Departement Approvisionnement',
      description: 'Vue d\'ensemble et opérations',
      icon: <StockIcon />,
      route: '/stock',
      available: true,
      color: '#2196F3',
      moduleName: 'Stock',
    },
    {
      id: 'configuration',
      title: 'Configuration',
      description: 'Gestion des produits, fournisseurs et paramètres',
      icon: <ConfigIcon />,
      route: '/configuration',
      available: true,
      color: '#1976D2',
      moduleName: 'Config',
    },
    {
      id: 'administration',
      title: 'Administration',
      description: 'Gestion des utilisateurs et sécurité système',
      icon: <AdminIcon />,
      route: '/administration',
      available: true,
      color: '#F44336',
      moduleName: 'Admin',
    },
    {
      id: 'commercial',
      title: 'Département commercial',
      description: 'Gestion des devis et offres commerciales',
      icon: <CommercialIcon />,
      route: '/commercial',
      available: true,
      color: '#4CAF50',
      moduleName: 'Commercial',
    },
    {
      id: 'hr',
      title: 'Ressources Humaines',
      description: 'Gestion du personnel, paie et documents RH',
      icon: <HRIcon />,
      route: '/hr',
      available: true,
      color: '#00897B',
      moduleName: 'HR',
    },
    {
      id: 'audit',
      title: 'Audit & Conformité',
      description: 'Contrôle qualité et audit interne',
      icon: <AuditIcon />,
      route: '/audit',
      available: false,
      color: '#9C27B0',
    },
    {
      id: 'financier',
      title: 'Département Financier',
      description: 'Encaissements, décaissements et trésorerie',
      icon: <FinanceIcon />,
      route: '/financier',
      available: true,
      color: '#9C27B0',
      moduleName: 'Financier',
    },
    {
      id: 'production',
      title: 'Département Production',
      description: 'Gestion des projets et services de production',
      icon: <ProductionIcon />,
      route: '/production',
      available: true,
      color: '#FF5722',
      moduleName: 'Production',
    },
    {
      id: 'comptoir',
      title: 'Comptoir',
      description: 'Offres commerciales et clients du comptoir',
      icon: <ComptoirIcon />,
      route: '/comptoir',
      available: true,
      color: '#3949AB',
      moduleName: 'Comptoir',
    },
    {
      id: 'messagerie',
      title: 'Messagerie',
      description: 'Communication interne et partage de fichiers',
      icon: <ChatIcon />,
      route: '/messagerie',
      available: true,
      color: '#0078D4',
    },
    {
      id: 'logistics',
      title: 'Logistique',
      description: 'Transport et gestion des entrepôts',
      icon: <LogisticsIcon />,
      route: '/logistics',
      available: false,
      color: '#607D8B',
    },
    {
      id: 'sales',
      title: 'Ventes & CRM',
      description: 'Gestion commerciale et relation client',
      icon: <SalesIcon />,
      route: '/sales',
      available: false,
      color: '#E91E63',
    },
    {
      id: 'maintenance',
      title: 'Maintenance',
      description: 'Maintenance préventive et corrective',
      icon: <MaintenanceIcon />,
      route: '/maintenance',
      available: false,
      color: '#795548',
    },
  ];

  const handleModuleClick = (module: ERPModule) => {
    if (module.available) {
      navigate(module.route);
    }
  };

  // Role-adaptive quick actions. Each module exposes its top create-shortcuts
  // (ordered by importance). An action only appears if the connected user's role
  // grants the "add" (create) bit on that action's specific mini-module — module
  // level is NOT enough (a Commercial user with only "Demandes Achat" must not see
  // "Entrée de stock"). We then round-robin across the granted modules and cap at
  // 5 — single-role users fill from their module, managers get a balanced mix.
  const quickActions = useMemo(() => {
    const QUICK_ACTION_CATALOG: {
      moduleName: string;
      color: string;
      actions: { label: string; route: string; miniModule: string; icon: React.ReactElement }[];
    }[] = [
      {
        moduleName: 'Commercial',
        color: '#4CAF50',
        actions: [
          { label: 'Nouveau devis', route: '/commercial/devis', miniModule: 'Devis', icon: <CommercialIcon /> },
          { label: 'Nouvelle facture', route: '/commercial/factures', miniModule: 'Factures', icon: <InvoiceIcon /> },
          { label: 'Bon de livraison', route: '/commercial/bons-livraison', miniModule: 'Bons Livraison', icon: <LogisticsIcon /> },
        ],
      },
      {
        moduleName: 'Stock',
        color: '#2196F3',
        actions: [
          { label: 'Sortie de stock', route: '/stock/exits', miniModule: 'Stock Exit', icon: <StockIcon /> },
          { label: 'Entrée de stock', route: '/stock/entries', miniModule: 'Stock Entry', icon: <StockIcon /> },
          { label: "Demande d'achat", route: '/stock/demandes-achat', miniModule: 'Demandes Achat', icon: <SalesIcon /> },
        ],
      },
      {
        moduleName: 'HR',
        color: '#00897B',
        actions: [
          { label: 'Pointage', route: '/hr/personnel/pointage', miniModule: 'Pointage du Personnel', icon: <PointageIcon /> },
          { label: 'Demande de congé', route: '/hr/documents/demande-conge', miniModule: 'Congés', icon: <HRIcon /> },
        ],
      },
      {
        moduleName: 'Financier',
        color: '#9C27B0',
        actions: [
          { label: 'Encaissement', route: '/financier/encaissements', miniModule: 'Encaissements', icon: <FinanceIcon /> },
          { label: 'Décaissement', route: '/financier/decaissements', miniModule: 'Decaissements', icon: <FinanceIcon /> },
        ],
      },
      {
        moduleName: 'Production',
        color: '#FF5722',
        actions: [
          { label: 'Nouveau projet', route: '/production/projets-services', miniModule: 'Projets', icon: <ProductionIcon /> },
          { label: 'Planning', route: '/production/planning', miniModule: 'Planning', icon: <ProductionIcon /> },
        ],
      },
    ];

    // Keep only the actions the role can actually create (per mini-module).
    const granted = QUICK_ACTION_CATALOG.map((g) => ({
      ...g,
      actions: g.actions.filter((a) => hasPermission(g.moduleName, 'add', a.miniModule)),
    })).filter((g) => g.actions.length > 0);

    const picked: { label: string; route: string; icon: React.ReactElement; color: string }[] = [];
    const MAX = 5;
    for (let round = 0; picked.length < MAX; round++) {
      let addedThisRound = false;
      for (const g of granted) {
        const action = g.actions[round];
        if (!action) continue;
        picked.push({ label: action.label, route: action.route, icon: action.icon, color: g.color });
        addedThisRound = true;
        if (picked.length === MAX) break;
      }
      if (!addedThisRound) break;
    }
    return picked;
  }, [hasPermission]);

  // Group modules: ones the user can open first, then restricted, then "coming soon".
  const { activeModules, lockedModules, comingSoonModules } = useMemo(() => {
    const active: ERPModule[] = [];
    const locked: ERPModule[] = [];
    const soon: ERPModule[] = [];
    erpModules.forEach((m) => {
      if (!m.available) soon.push(m);
      else if (m.moduleName && !canAccessModule(m.moduleName)) locked.push(m);
      else active.push(m);
    });
    return { activeModules: active, lockedModules: locked, comingSoonModules: soon };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canAccessModule]);

  const firstName = user?.full_name?.trim().split(/\s+/)[0] || '';
  const greeting = firstName ? `Bonjour, ${firstName}` : 'Bonjour';
  const todayLabel = (() => {
    const raw = format(new Date(), 'EEEE d MMMM yyyy', { locale: fr });
    return raw.charAt(0).toUpperCase() + raw.slice(1);
  })();

  const handleLogout = async () => {
    setProfileAnchorEl(null);
    await logout();
    navigate('/login');
  };

  const handleToggleTheme = () => {
    toggleTheme();
    setProfileAnchorEl(null);
  };

  const renderModuleCard = (module: ERPModule) => {
    const hasAccess = module.moduleName ? canAccessModule(module.moduleName) : true;
    const isAccessible = module.available && hasAccess;

    return (
      <Grid item xs={12} sm={6} md={4} lg={3} key={module.id}>
        <GlassSurface
          accentColor={isAccessible ? module.color : undefined}
          interactive={isAccessible}
          onClick={isAccessible ? () => handleModuleClick(module) : undefined}
          sx={{ p: 2, opacity: isAccessible ? 1 : 0.6 }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              height: '100%',
              minHeight: 180,
              position: 'relative',
            }}
          >
            <Avatar
              sx={{
                bgcolor: isAccessible ? module.color : 'grey.400',
                width: 64,
                height: 64,
                mb: 2,
              }}
            >
              {React.cloneElement(module.icon, {
                sx: { fontSize: 32, color: 'white' },
              })}
            </Avatar>

            <CardContent sx={{ textAlign: 'center', pb: 1 }}>
              <Typography
                gutterBottom
                variant="h6"
                component="h3"
                color={isAccessible ? 'text.primary' : 'text.disabled'}
              >
                {module.title}
              </Typography>
              <Typography
                variant="body2"
                color={isAccessible ? 'text.secondary' : 'text.disabled'}
                sx={{ minHeight: 40 }}
              >
                {module.description}
              </Typography>
            </CardContent>

            {!module.available && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 8,
                  right: 8,
                  bgcolor: 'warning.main',
                  color: 'white',
                  px: 1,
                  py: 0.5,
                  borderRadius: 1,
                  fontSize: '0.75rem',
                }}
              >
                Bientôt
              </Box>
            )}

            {module.available && !hasAccess && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 8,
                  right: 8,
                  bgcolor: 'error.main',
                  color: 'white',
                  px: 1,
                  py: 0.5,
                  borderRadius: 1,
                  fontSize: '0.75rem',
                }}
              >
                Accès restreint
              </Box>
            )}
          </Box>
        </GlassSurface>
      </Grid>
    );
  };

  return (
    <Box sx={{ flexGrow: 1 }}>
      {/* Floating top-right action cluster */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'center',
          gap: 1,
          px: 3,
          pt: 2,
          color: 'primary.main',
        }}
      >
        <HeaderChatButton />
        <NotificationsBell />
        <IconButton
          color="inherit"
          size="large"
          onClick={(e) => setProfileAnchorEl(e.currentTarget)}
        >
          <AccountCircleIcon />
        </IconButton>
        <Menu
          anchorEl={profileAnchorEl}
          open={profileOpen}
          onClose={() => setProfileAnchorEl(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <Box sx={{ px: 2, py: 1 }}>
            <Typography variant="subtitle2" fontWeight={700}>
              {user?.full_name || 'Utilisateur'}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {user?.email}
            </Typography>
          </Box>
          <Divider />
          <MenuItem onClick={handleToggleTheme}>
            <ListItemIcon>
              {mode === 'dark' ? <Brightness7Icon fontSize="small" /> : <Brightness4Icon fontSize="small" />}
            </ListItemIcon>
            {mode === 'dark' ? 'Mode clair' : 'Mode sombre'}
          </MenuItem>
          <MenuItem onClick={handleLogout}>
            <ListItemIcon>
              <LogoutIcon fontSize="small" />
            </ListItemIcon>
            Déconnexion
          </MenuItem>
        </Menu>
      </Box>

      {/* Main Content */}
      <Container maxWidth="xl" sx={{ mt: 0, mb: 4 }}>
        <Box sx={{ mb: 4, textAlign: 'center' }}>
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
            <img
              src={logo}
              alt="M.A.F Agencement Logo"
              style={{ height: '100px', width: 'auto' }}
            />
          </Box>

          <Typography variant="h5" fontWeight={700} color="text.primary">
            {greeting}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {todayLabel}
          </Typography>
        </Box>

        {quickActions.length > 0 && (
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              justifyContent: 'center',
              gap: 1.5,
              mb: 5,
            }}
          >
            {quickActions.map((action) => (
              <Button
                key={action.label}
                variant="outlined"
                startIcon={action.icon}
                onClick={() => navigate(action.route)}
                sx={{
                  textTransform: 'none',
                  borderRadius: 2,
                  px: 2,
                  py: 1,
                  color: action.color,
                  borderColor: action.color,
                  bgcolor: 'background.paper',
                  '&:hover': {
                    borderColor: action.color,
                    bgcolor: `${action.color}14`,
                  },
                }}
              >
                {action.label}
              </Button>
            ))}
          </Box>
        )}

      <GlassBackdrop>
        <Grid container spacing={3}>
          {activeModules.map(renderModuleCard)}
          {lockedModules.map(renderModuleCard)}
        </Grid>

        {comingSoonModules.length > 0 && (
          <Box sx={{ mt: 5 }}>
            <Divider sx={{ mb: 3 }}>
              <Typography variant="overline" color="text.secondary">
                À venir
              </Typography>
            </Divider>
            <Grid container spacing={3}>
              {comingSoonModules.map(renderModuleCard)}
            </Grid>
          </Box>
        )}
      </GlassBackdrop>
      </Container>
    </Box>
  );
};

export default MainDashboard;